import { JsonbEditorBase } from "./jsonbEditorBase.js";

const { autorun, observable } = mobx;

/**
 * Create a reusable object editor for JSONB cell data in Supabase
 * 
 * @param {Object} config
 * @param {Object} config.dom - D3 selection for the container
 * @param {Object} config.message - Message utilities {bad, info, progress, good}
 * @param {string} config.table - Supabase table name
 * @param {string} config.recordId - Record ID to update
 * @param {string} config.fieldName - JSONB field name containing the object
 * @param {string} [config.subPath] - Path to sub-object within the JSONB field (e.g., 'Footer')
 * @param {string} [config.base_url] - Base URL for relative image paths (display only)
 * @param {Object} config.schema - Field definitions as object with keys
 * @param {string} config.schema[key].type - Field type: 'text', 'image', 'url', 'number', 'color', 'kvp'
 * @param {string} config.schema[key].label - Label for display and editing
 * @param {Array<string>} [config.schema[key].keys] - For 'kvp' type: array of allowed keys
 * @param {Function} [config.onBeforeSave] - Transform object before save (obj) => obj
 * @param {Function} [config.onAfterLoad] - Transform object after load (obj) => obj
 * @param {Function} [config.validate] - Validate object before save (obj) => errorMessage|null
 */
export function createJsonbObjectEditor(config) {
  const editor = new JsonbObjectEditor(config);
  return {
    load: () => editor.load(),
    refresh: () => editor.load(),
    getData: () => editor.state.data,
    setData: (data) => editor.saveAndReload(data)
  };
}

class JsonbObjectEditor extends JsonbEditorBase {
  constructor(config) {
    super(config);
    this.schema = config.schema || {};
    this.subPath = config.subPath; // Optional path to sub-object
    this.allowEdit = config.allowEdit !== undefined ? config.allowEdit : true;
    
    this.state.mode = 'view'; // 'view' or 'edit'
    this.state.fullData = null; // Store full JSONB data when using subPath
  }

  async saveAndReload(data) {
    const { error } = await this.saveData(data);
    if (error) {
      console.error('Error saving data', error);
      this.message.bad('Error saving data');
      return;
    }
    await this.load();
  }

  async load() {
    this.state.isLoading = true;
    this.state.error = null;
    
    const { data, error } = await this.fetchData();
    
    if (error) {
      this.state.error = error;
      console.error('Error loading data', error);
      this.dom.html('');
      this.dom.append('p').text('Error loading data.');
      return;
    }
    
    // If subPath is specified, extract the sub-object
    if (this.subPath) {
      this.state.fullData = data || {};
      this.state.data = data?.[this.subPath] || {};
      this.state.originalData = JSON.parse(JSON.stringify(this.state.data));
    } else {
      this.state.data = data || {};
      this.state.originalData = JSON.parse(JSON.stringify(data || {}));
      this.state.fullData = null;
    }
    
    this.state.isLoading = false;
    this.render();
  }

  cancelEdit() {
    this.state.data = JSON.parse(JSON.stringify(this.state.originalData));
    this.state.mode = 'view';
    this.render();
  }

  render() {
    this.dom.html('');

    const wrapper = this.dom.append('section').attr('class', 'jsonb-object-editor-wrapper');
    const headerElement = wrapper.append("div").class("admin-section-header");
    if(this.heading) headerElement.append("h1").text(this.heading);
    if(this.comment) wrapper.append("p").style("color", "#999").style("margin-bottom", "10px").text(this.comment);
    const table = wrapper.append('table').attr('class', 'admin-table object-editor');

    // Header
    const headerRow = table.append('tr');
    headerRow.append('th').text('Key');
    headerRow.append('th').text('Value');

    autorun(() => {
      // Remove existing data rows
      table.selectAll('tr.data-row').remove();

      if (this.state.mode === 'view') {
        this.renderViewMode(table);
      } else {
        this.renderEditMode(table);
      }
    });

    // Action buttons
    const buttonsDiv = headerElement.append('div').attr('class', 'actionsbox');
    
    autorun(() => {
      buttonsDiv.html('');
      
      if (this.state.mode === 'view') {
        buttonsDiv.append('span')
          .text('Edit')
          .class('button bigger')
          .classed('disabled', !this.allowEdit)
          .on('click', () => {
            this.state.mode = 'edit';
            this.render();
          });
      } else {
        buttonsDiv.append('span')
          .text('Cancel')
          .attr('class', 'button')
          .on('click', () => this.cancelEdit());

        buttonsDiv.append('span')
          .text('Save Changes')
          .attr('class', 'button addnew')
          .on('click', () => this.saveChanges());
      }
    });
  }

  renderViewMode(table) {
    const data = this.state.data || {};
    const keys = Object.keys(this.schema);

    if (keys.length === 0) {
      const emptyRow = table.append('tr').attr('class', 'data-row empty-row');
      emptyRow.append('td')
        .attr('colspan', 2)
        .style('text-align', 'center')
        .style('padding', '20px')
        .style('color', '#999')
        .style('font-style', 'italic')
        .text('No fields defined in schema');
      return;
    }

    keys.forEach(key => {
      const fieldDef = { ...this.schema[key], key, field: key };
      const value = data[key];
      
      const row = table.append('tr').attr('class', 'data-row');
      
      // Key column
      row.append('td')
        .attr('class', 'key-cell')
        .style('font-weight', 'bold')
        .style('width', '30%')
        .text(fieldDef.label || key);
      
      // Value column
      const valueCell = row.append('td').attr('class', 'value-cell');
      
      if (value !== null && value !== undefined && value !== '') {
        const container = valueCell.append('div');
        this.renderFieldValue(container, fieldDef, value, false);
      } else {
        valueCell
          .style('color', '#999')
          .style('font-style', 'italic')
          .text('(not set)');
      }
    });
  }

  renderEditMode(table) {
    const data = this.state.data || {};
    const keys = Object.keys(this.schema);

    if (keys.length === 0) {
      const emptyRow = table.append('tr').attr('class', 'data-row empty-row');
      emptyRow.append('td')
        .attr('colspan', 2)
        .style('text-align', 'center')
        .style('padding', '20px')
        .style('color', '#999')
        .style('font-style', 'italic')
        .text('No fields defined in schema');
      return;
    }

    keys.forEach(key => {
      const fieldDef = { ...this.schema[key], key, field: key };
      const value = data[key];
      
      const row = table.append('tr').attr('class', 'data-row edit-row');
      
      // Key column
      row.append('th')
        .text(fieldDef.label || key);
      
      // Input column
      const inputCell = row.append('td');
      this.renderFormInput(inputCell, fieldDef, 'edit', value);
    });
  }

  async saveChanges() {
    const form = this.dom;
    const newData = {};
    
    Object.keys(this.schema).forEach(key => {
      const fieldDef = { ...this.schema[key], key, field: key };
      const value = this.collectFormValue(form, fieldDef, 'edit');
      if (value !== null && value !== undefined && value !== '') {
        newData[key] = value;
      }
    });

    // Validate payload
    const validationError = this.validatePayload(newData);
    if (validationError) {
      this.message.bad(validationError);
      return;
    }

    if (this.validate) {
      const error = this.validate(newData);
      if (error) {
        this.message.bad(error);
        return;
      }
    }

    // If subPath is specified, merge the sub-object back into full data
    let dataToSave;
    if (this.subPath) {
      dataToSave = { ...this.state.fullData, [this.subPath]: newData };
    } else {
      dataToSave = newData;
    }

    const { error } = await this.saveData(dataToSave);
    
    if (error) {
      console.error('Error saving changes', error);
      this.message.bad('Error saving changes');
      return;
    }
    
    this.state.mode = 'view';
    await this.load();
  }

  validatePayload(payload) {
    for (const key of Object.keys(this.schema)) {
      const fieldDef = this.schema[key];
      const value = payload[key];
      
      // Check required fields
      if (fieldDef.required) {
        if (fieldDef.type === 'boolean') {
          if (typeof value !== 'boolean') {
            return `${fieldDef.label || key} is required and must be true or false`;
          }
        } else if (fieldDef.type === 'textarray') {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return `${fieldDef.label || key} is required`;
          }
        } else if (fieldDef.type === 'options') {
          if (!value) {
            return `${fieldDef.label || key} is required`;
          }
        } else {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            return `${fieldDef.label || key} is required`;
          }
        }
      }
      
      // Validate options type against allowed values
      if (fieldDef.type === 'options' && fieldDef.values && value) {
        if (!fieldDef.values.includes(value)) {
          return `${fieldDef.label || key} must be one of: ${fieldDef.values.join(', ')}`;
        }
      }
      
      // Validate textarray type against allowed values
      if (fieldDef.type === 'textarray' && fieldDef.values && value && Array.isArray(value)) {
        for (const item of value) {
          if (!fieldDef.values.includes(item)) {
            return `${fieldDef.label || key} contains invalid value: ${item}. Must be one of: ${fieldDef.values.join(', ')}`;
          }
        }
      }
    }
    return null;
  }
}
